import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import { createRegistryResolver, tierSource } from './resolveCore';
import type { RegistryResolver, ResolverFactoryOptions } from './types';

export interface SedSectionResolverInput extends ResolverFactoryOptions {
  sed?: SiteEvidenceDocument;
  reportDerived?: Record<string, { value: unknown; reportId: string }>;
  llmSlots?: Record<string, unknown>;
}

export function createSedSectionResolver(opts: SedSectionResolverInput): RegistryResolver {
  const fieldSources: Record<string, ReturnType<typeof tierSource>[]> = {};
  const sed = opts.sed;

  if (sed) {
    for (const [sectionId, section] of Object.entries(sed)) {
      if (!section || typeof section !== 'object' || !('data' in section)) continue;
      const slot = section as { data?: Record<string, unknown>; source?: string; provenance?: string };
      if (!slot.data) continue;
      for (const [fieldKey, value] of Object.entries(slot.data)) {
        if (value === undefined || value === null) continue;
        const path = `${sectionId}.${fieldKey}`;
        const sources = [
          tierSource(1, value, {
            kind: 'provider',
            ref: `sed:${path}`,
            label: slot.source ?? sectionId,
          }),
        ];
        const reportSlot = opts.reportDerived?.[path];
        if (reportSlot) {
          sources.push(
            tierSource(2, reportSlot.value, {
              kind: 'report',
              ref: `report:${reportSlot.reportId}:${path}`,
              label: 'Prior report derived',
            }),
          );
        }
        const llmVal = opts.llmSlots?.[path];
        if (llmVal !== undefined) {
          sources.push(
            tierSource(3, llmVal, {
              kind: 'llm-inference',
              ref: `llm:sed:${path}`,
              label: 'LLM slot inference',
            }),
          );
        }
        fieldSources[path] = sources;
      }
    }
  }

  return createRegistryResolver({
    registryId: 'sedSection',
    siteId: opts.siteId,
    fieldSources,
    now: opts.now,
  });
}
